import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Flex, Heading, Text, Button, Input, InputGroup, InputLeftElement, IconButton,
  VStack, HStack, Badge, useToast, Spinner, Divider, Container, SimpleGrid,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton,
  FormControl, FormLabel, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
  Icon, Wrap, WrapItem, Tag, TagLabel, useBreakpointValue
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, LockIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { FaCamera, FaBarcode, FaMapMarkerAlt } from 'react-icons/fa';
import BarcodeScanner from '../components/BarcodeScanner';
import ProdutoImagem from '../components/ProdutoImagem';
import ScrollToTop from '../components/ScrollToTop';
import { buscarProdutoEstoqueMobile, entradaEstoqueMobile } from '../services/api';

const STORAGE_KEY_TRAVADA = 'taga_entrada_estoque_posicao_travada';

const beep = (() => {
  let ctx = null;
  return (freq = 880, duration = 120) => {
    try {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
      osc.start();
      osc.stop(ctx.currentTime + duration / 1000);
    } catch {}
  };
})();

const beepSucesso = () => { beep(880, 100); setTimeout(() => beep(1320, 120), 110); };
const beepErro    = () => { beep(220, 250); };

const formatBRL = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const CodigoBadges = ({ p, size = '2xs' }) => (
  <Wrap spacing={1.5} shouldWrapChildren>
    <Badge colorScheme="blue" fontSize={size}>SKU {p.produto_id}</Badge>
    {p.codigo_global ? <Badge colorScheme="purple" fontSize={size}>{p.codigo_global}</Badge> : null}
    {p.codigo_barra ? (
      <Badge colorScheme="gray" fontSize={size} textTransform="none">
        <Icon as={FaBarcode} boxSize={2.5} mr={1} mb="-1px" />{p.codigo_barra}
      </Badge>
    ) : null}
  </Wrap>
);

const InfoLinha = ({ label, value }) => (
  value ? (
    <Flex justify="space-between" gap={3} py={1}>
      <Text color="gray.500" fontSize="xs" flexShrink={0}>{label}</Text>
      <Text fontSize="xs" fontWeight="semibold" textAlign="right" noOfLines={1}>{value}</Text>
    </Flex>
  ) : null
);

const ResultadoCard = ({ p, onClick }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="xl"
    p={3}
    role="button"
    cursor="pointer"
    onClick={() => onClick(p)}
    _hover={{ borderColor: 'orange.300', boxShadow: 'md' }}
    _active={{ bg: 'orange.50', transform: 'scale(0.995)' }}
    transition="all 0.12s"
  >
    <HStack spacing={3} align="center">
      <Box w="64px" h="64px" flexShrink={0} borderRadius="lg" overflow="hidden" bg="gray.50">
        <ProdutoImagem produto={{ produto_id: p.produto_id, nome: p.nome }} width="64px" height="64px" />
      </Box>
      <Box flex={1} minW={0}>
        <Text fontWeight="bold" fontSize="sm" noOfLines={2} lineHeight="1.3">{p.nome}</Text>
        <Box mt={1.5}><CodigoBadges p={p} /></Box>
        <HStack spacing={2} mt={1.5} flexWrap="wrap">
          <Badge colorScheme={p.quantidade_taga > 0 ? 'green' : 'gray'} fontSize="2xs">
            Estoque: {p.quantidade_taga}
          </Badge>
          {p.localizacoes?.length > 0 ? (
            <Badge colorScheme="orange" fontSize="2xs">
              {p.localizacoes.length} {p.localizacoes.length === 1 ? 'local' : 'locais'}
            </Badge>
          ) : null}
          {p.bloqueado ? <Badge colorScheme="red" fontSize="2xs">Bloqueado</Badge> : null}
        </HStack>
      </Box>
    </HStack>
  </Box>
);

const EntradaEstoqueMobile = () => {
  const toast = useToast();
  const [termo, setTermo] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [erroBusca, setErroBusca] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const [posicaoTravada, setPosicaoTravada] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_TRAVADA) || ''; } catch { return ''; }
  });

  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [qtd, setQtd] = useState(1);
  const [localizacao, setLocalizacao] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [ultimoSucesso, setUltimoSucesso] = useState(null);
  const inputBuscaRef = useRef(null);

  const isDesktop = useBreakpointValue({ base: false, lg: true });

  useEffect(() => {
    try {
      if (posicaoTravada) localStorage.setItem(STORAGE_KEY_TRAVADA, posicaoTravada);
      else localStorage.removeItem(STORAGE_KEY_TRAVADA);
    } catch {}
  }, [posicaoTravada]);

  const executarBusca = async (termoBusca) => {
    const t = (termoBusca || '').trim();
    if (!t) {
      setResultados([]);
      setErroBusca(null);
      return;
    }
    setBuscando(true);
    setErroBusca(null);
    const res = await buscarProdutoEstoqueMobile(t);
    setBuscando(false);

    if (res?.error) {
      setErroBusca(res.error);
      setResultados([]);
      beepErro();
      return;
    }

    const itens = res?.itens || [];
    setResultados(itens);

    if (itens.length === 0) {
      setErroBusca(`Nenhum produto encontrado para "${t}".`);
      beepErro();
    } else if (itens.length === 1) {
      abrirConfirmacao(itens[0]);
    }
  };

  const handleBuscaSubmit = (e) => {
    e?.preventDefault?.();
    executarBusca(termo);
  };

  const handleScanDetected = (codigo) => {
    setScannerOpen(false);
    setTermo(codigo);
    executarBusca(codigo);
  };

  const abrirConfirmacao = (produto) => {
    setProdutoSelecionado(produto);
    setQtd(1);
    setLocalizacao(posicaoTravada || (produto.localizacoes?.[0]?.localizacao || ''));
    setDrawerOpen(true);
  };

  const fecharConfirmacao = () => {
    if (salvando) return;
    setDrawerOpen(false);
    setProdutoSelecionado(null);
  };

  const validarLocalizacao = (loc) => {
    const l = (loc || '').trim();
    if (!l) return 'Informe a localização';
    if (l.length > 255) return 'Localização muito longa';
    if (l.includes('|') || l.includes(':')) return 'Localização não pode conter | ou :';
    return null;
  };

  const handleSalvar = async () => {
    if (!produtoSelecionado) return;
    const erroLoc = validarLocalizacao(localizacao);
    if (erroLoc) {
      toast({ title: erroLoc, status: 'warning', duration: 2500 });
      beepErro();
      return;
    }
    if (!Number.isInteger(qtd) || qtd < 1) {
      toast({ title: 'Quantidade inválida', status: 'warning', duration: 2500 });
      beepErro();
      return;
    }

    setSalvando(true);
    const res = await entradaEstoqueMobile({
      produto_id: produtoSelecionado.produto_id,
      quantidade: qtd,
      localizacao: localizacao.trim(),
    });
    setSalvando(false);

    if (res?.error) {
      toast({
        title: 'Erro ao registrar entrada',
        description: res.error,
        status: 'error',
        duration: 4000,
      });
      beepErro();
      return;
    }

    beepSucesso();
    setUltimoSucesso({
      nome: produtoSelecionado.nome,
      produto_id: produtoSelecionado.produto_id,
      quantidade: qtd,
      localizacao: localizacao.trim(),
      quantidade_local_total: res.quantidade_local_total,
      quantidade_taga_total: res.quantidade_taga_total,
      at: new Date(),
    });

    toast({
      title: 'Entrada registrada!',
      description: `${qtd}x ${produtoSelecionado.nome} em ${localizacao.trim()}`,
      status: 'success',
      duration: 2500,
    });

    setDrawerOpen(false);
    setProdutoSelecionado(null);
    setTermo('');
    setResultados([]);
    setErroBusca(null);

    setTimeout(() => inputBuscaRef.current?.focus(), 100);
  };

  const travarPosicaoAtual = () => {
    const erroLoc = validarLocalizacao(localizacao);
    if (erroLoc) {
      toast({ title: erroLoc, status: 'warning', duration: 2500 });
      return;
    }
    setPosicaoTravada(localizacao.trim());
    toast({
      title: `Posição "${localizacao.trim()}" travada`,
      description: 'Próximas entradas usarão esta posição automaticamente',
      status: 'info',
      duration: 2500,
    });
  };

  const destravarPosicao = () => {
    setPosicaoTravada('');
    toast({ title: 'Posição destravada', status: 'info', duration: 1500 });
  };

  const podeUsarSugestao = produtoSelecionado?.localizacoes?.length > 0;

  const bannerPosicaoTravada = posicaoTravada ? (
    <HStack spacing={1} bg="#F68633" color="white" pl={3} pr={1} py={1} borderRadius="full" flexShrink={0} boxShadow="sm">
      <Icon as={FaMapMarkerAlt} boxSize={3} />
      <Box lineHeight="1.05" pr={1}>
        <Text fontSize="2xs" opacity={0.9} textTransform="uppercase" letterSpacing="0.5px">Travado</Text>
        <Text fontSize="sm" fontWeight="bold">{posicaoTravada}</Text>
      </Box>
      <IconButton
        aria-label="Destravar posição"
        icon={<CloseIcon boxSize={2} />}
        size="xs"
        isRound
        variant="ghost"
        color="white"
        _hover={{ bg: 'whiteAlpha.300' }}
        onClick={destravarPosicao}
      />
    </HStack>
  ) : (
    <Badge colorScheme="gray" variant="subtle" fontSize="2xs" px={2} py={1} borderRadius="full" flexShrink={0}>
      Sem posição travada
    </Badge>
  );

  const ultimoSucessoCard = ultimoSucesso ? (
    <Box bg="green.50" border="1px solid" borderColor="green.200" p={4} borderRadius="xl">
      <HStack mb={2} spacing={2}>
        <Icon as={CheckCircleIcon} color="green.500" />
        <Text fontSize="sm" fontWeight="bold" color="green.800">Última entrada registrada</Text>
      </HStack>
      <Text fontSize="sm" color="green.800" noOfLines={2}>
        <strong>{ultimoSucesso.quantidade}×</strong> {ultimoSucesso.nome}
      </Text>
      <HStack mt={2} spacing={2} flexWrap="wrap">
        <Badge colorScheme="green" fontSize="2xs">
          <Icon as={FaMapMarkerAlt} boxSize={2.5} mr={1} mb="-1px" />
          {ultimoSucesso.localizacao} · {ultimoSucesso.quantidade_local_total}
        </Badge>
        <Badge colorScheme="green" variant="solid" fontSize="2xs">
          Estoque total: {ultimoSucesso.quantidade_taga_total}
        </Badge>
      </HStack>
    </Box>
  ) : null;

  const conteudoConfirmacao = produtoSelecionado ? (
    <VStack spacing={4} align="stretch">
      <HStack spacing={3} align="flex-start" bg="gray.50" p={3} borderRadius="lg">
        <Box w="72px" h="72px" flexShrink={0} borderRadius="lg" overflow="hidden" bg="white">
          <ProdutoImagem
            produto={{ produto_id: produtoSelecionado.produto_id, nome: produtoSelecionado.nome }}
            width="72px"
            height="72px"
          />
        </Box>
        <Box flex={1} minW={0}>
          <Text fontWeight="bold" fontSize="sm" noOfLines={3} lineHeight="1.3">{produtoSelecionado.nome}</Text>
          <HStack spacing={2} mt={1.5} flexWrap="wrap">
            <Badge colorScheme={produtoSelecionado.quantidade_taga > 0 ? 'green' : 'gray'} fontSize="2xs">
              Estoque atual: {produtoSelecionado.quantidade_taga}
            </Badge>
            {produtoSelecionado.bloqueado ? <Badge colorScheme="red" fontSize="2xs">Bloqueado</Badge> : null}
          </HStack>
        </Box>
      </HStack>

      <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={6} spacingY={0} borderWidth="1px" borderColor="gray.100" borderRadius="lg" px={3} py={1}>
        <InfoLinha label="SKU" value={`#${produtoSelecionado.produto_id}`} />
        <InfoLinha label="Cód. Global" value={produtoSelecionado.codigo_global} />
        <InfoLinha label="EAN / Barra" value={produtoSelecionado.codigo_barra} />
        <InfoLinha label="Cód. Fornecedor" value={produtoSelecionado.codigo_fornecedor} />
        <InfoLinha label="Cód. Fabricante" value={produtoSelecionado.codigo_fabricante} />
        <InfoLinha label="Fabricante" value={produtoSelecionado.nome_fabricante} />
        <InfoLinha label="Valor de venda" value={formatBRL(produtoSelecionado.valor_venda)} />
      </SimpleGrid>

      <Divider />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1.5}>Quantidade de entrada</FormLabel>
          <NumberInput
            size="lg"
            min={1}
            value={qtd}
            onChange={(_, v) => setQtd(Number.isFinite(v) && v >= 1 ? v : 1)}
          >
            <NumberInputField fontSize="2xl" textAlign="center" fontWeight="bold" h="60px" />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <HStack spacing={2} mt={2}>
            {[1, 2, 5, 10].map((n) => (
              <Button
                key={n}
                flex={1}
                size="sm"
                variant={qtd === n ? 'solid' : 'outline'}
                colorScheme="orange"
                onClick={() => setQtd(n)}
              >
                {n}
              </Button>
            ))}
          </HStack>
        </FormControl>

        <FormControl isRequired>
          <Flex justify="space-between" align="center" mb={1.5}>
            <FormLabel fontSize="sm" m={0}>Localização</FormLabel>
            {posicaoTravada ? (
              <HStack spacing={1} color="orange.600">
                <LockIcon boxSize={3} />
                <Text fontSize="2xs" fontWeight="bold">TRAVADA</Text>
              </HStack>
            ) : (
              <Button
                size="xs"
                variant="outline"
                colorScheme="orange"
                leftIcon={<LockIcon boxSize={3} />}
                onClick={travarPosicaoAtual}
                isDisabled={!localizacao.trim()}
              >
                Travar posição
              </Button>
            )}
          </Flex>
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none" h="60px">
              <Icon as={FaMapMarkerAlt} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Ex: A-4-4"
              value={localizacao}
              onChange={(e) => setLocalizacao(e.target.value)}
              fontSize="lg"
              fontWeight="bold"
              textTransform="uppercase"
              isDisabled={!!posicaoTravada}
              h="60px"
            />
          </InputGroup>
          {posicaoTravada ? (
            <Text fontSize="2xs" color="gray.500" mt={1}>
              Destrave no topo da tela para alterar a posição
            </Text>
          ) : null}
        </FormControl>
      </SimpleGrid>

      {podeUsarSugestao && !posicaoTravada ? (
        <Box>
          <Text fontSize="xs" color="gray.600" mb={1.5}>Posições já usadas deste produto:</Text>
          <Wrap spacing={2}>
            {produtoSelecionado.localizacoes.map((l) => (
              <WrapItem key={l.id}>
                <Tag
                  size="lg"
                  colorScheme="blue"
                  borderRadius="full"
                  cursor="pointer"
                  onClick={() => setLocalizacao(l.localizacao)}
                  _hover={{ bg: 'blue.100' }}
                  _active={{ bg: 'blue.200' }}
                >
                  <Icon as={FaMapMarkerAlt} boxSize={3} mr={1} />
                  <TagLabel>{l.localizacao} · {l.quantidade}</TagLabel>
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      ) : null}
    </VStack>
  ) : null;

  const buscaCard = (
    <Box
      as="form"
      onSubmit={handleBuscaSubmit}
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      p={{ base: 4, lg: 5 }}
    >
      <VStack spacing={3} align="stretch">
        <Button
          type="button"
          leftIcon={<Icon as={FaCamera} boxSize={5} />}
          colorScheme="orange"
          size="lg"
          h={{ base: '60px', lg: '56px' }}
          fontSize="md"
          boxShadow="sm"
          onClick={() => setScannerOpen(true)}
        >
          Escanear código de barras
        </Button>
        <Flex align="center" gap={3}>
          <Divider />
          <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">ou busca manual</Text>
          <Divider />
        </Flex>
        <Flex gap={2} direction={{ base: 'column', sm: 'row' }}>
          <InputGroup size="lg" flex={1}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              ref={inputBuscaRef}
              placeholder="Código, SKU, cód. global ou nome"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              bg="gray.50"
              fontSize="md"
              enterKeyHint="search"
              autoFocus
            />
          </InputGroup>
          <Button
            type="submit"
            colorScheme="orange"
            variant="outline"
            size="lg"
            px={6}
            leftIcon={<SearchIcon />}
            isLoading={buscando}
            loadingText="Buscando"
            w={{ base: '100%', sm: 'auto' }}
          >
            Buscar
          </Button>
        </Flex>
      </VStack>
    </Box>
  );

  return (
    <Box bg="gray.50" minH="100vh" py={6}>
      <ScrollToTop />
      <Container maxW="container.xl">
        <Box maxW="780px" mx="auto">
          <Flex
            justify="space-between"
            align={{ base: 'flex-start', sm: 'center' }}
            direction={{ base: 'column', sm: 'row' }}
            gap={3}
            mb={6}
          >
            <Box>
              <Heading size="lg" color="orange.500">Entrada de Estoque</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Leitura por código de barras — estoque físico TAGA
              </Text>
            </Box>
            {bannerPosicaoTravada}
          </Flex>

          <VStack spacing={4} align="stretch">
            {buscaCard}

            {buscando ? (
              <Flex justify="center" py={6}>
                <Spinner color="orange.400" size="lg" thickness="3px" />
              </Flex>
            ) : null}

            {erroBusca && !buscando ? (
              <Box bg="red.50" border="1px solid" borderColor="red.200" p={3} borderRadius="lg">
                <Text color="red.700" fontSize="sm">{erroBusca}</Text>
              </Box>
            ) : null}

            {resultados.length > 1 ? (
              <Box>
                <Text fontSize="sm" color="gray.600" fontWeight="semibold" mb={2}>
                  {resultados.length} resultados — toque para selecionar
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  {resultados.map((p) => (
                    <ResultadoCard key={p.produto_estoque_id} p={p} onClick={abrirConfirmacao} />
                  ))}
                </SimpleGrid>
              </Box>
            ) : null}

            {ultimoSucessoCard}
          </VStack>
        </Box>
      </Container>

      <BarcodeScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleScanDetected}
      />

      {isDesktop ? (
        <Modal
          isOpen={drawerOpen}
          onClose={fecharConfirmacao}
          closeOnOverlayClick={!salvando}
          closeOnEsc={!salvando}
          size="xl"
          isCentered
        >
          <ModalOverlay />
          <ModalContent borderRadius="xl">
            <ModalHeader fontSize="md" pb={2}>Confirmar entrada de estoque</ModalHeader>
            <ModalCloseButton isDisabled={salvando} />
            <ModalBody pb={4}>{conteudoConfirmacao}</ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
              <Button variant="ghost" onClick={fecharConfirmacao} isDisabled={salvando}>
                Cancelar
              </Button>
              <Button
                colorScheme="green"
                onClick={handleSalvar}
                isLoading={salvando}
                loadingText="Salvando..."
                leftIcon={<CheckCircleIcon />}
              >
                Salvar entrada
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ) : (
        <Drawer
          isOpen={drawerOpen}
          placement="bottom"
          onClose={fecharConfirmacao}
          closeOnOverlayClick={!salvando}
          closeOnEsc={!salvando}
        >
          <DrawerOverlay />
          <DrawerContent borderTopRadius="2xl" maxH="94vh">
            <DrawerCloseButton isDisabled={salvando} />
            <DrawerHeader pb={2}>
              <Text fontSize="md" fontWeight="bold">Confirmar entrada</Text>
            </DrawerHeader>
            <DrawerBody pb={4}>{conteudoConfirmacao}</DrawerBody>
            <DrawerFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
              <Button variant="ghost" onClick={fecharConfirmacao} isDisabled={salvando} flex={1}>
                Cancelar
              </Button>
              <Button
                colorScheme="green"
                onClick={handleSalvar}
                isLoading={salvando}
                loadingText="Salvando..."
                leftIcon={<CheckCircleIcon />}
                flex={2}
                size="lg"
              >
                Salvar entrada
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
};

export default EntradaEstoqueMobile;
